import { isMobile } from 'react-device-detect';

import {
    CHAT_MAX_MESSAGES_UNREGISTER,
    CHAT_TIME_LIMIT_SECONDS,
    STORAGE_KEY_CHATS_MESSAGES_COUNT,
    STORAGE_KEY_RESEND_MESSAGES
} from '../const';
import StorageService from '../service/StorageService';
import { ensureAnonMessagesRolled } from '../service/anonMessageCredits';
import { store } from '../store';
import { setChatHeaderCounter } from '../store/slices/chatsSlice';

export const onLinkShareClick = (url, cb) => {
    const shareData = {
        url
    };

    if (navigator.share && isMobile) {
        navigator
            .share(shareData)
            .then(() => {
                navigator.clipboard.writeText(url).then(cb);
            })
            .catch(() => {
                navigator.clipboard.writeText(url).then(cb);
            });
    } else {
        navigator.clipboard.writeText(url).then(cb);
    }
};

export function debounce(func, wait, immediate = false) {
    let timeout;

    return function executedFunction(...args) {
        const later = function () {
            timeout = null;
            if (!immediate) {
                func(...args);
            }
        };

        const callNow = immediate && !timeout;

        clearTimeout(timeout);

        timeout = setTimeout(later, wait);

        if (callNow) {
            func(args);
        }
    };
}

export const arrayToQueryString = (key, arr) => {
    if (!Array.isArray(arr)) {
        return '';
    }

    const values = arr.map((value) => `${key}=${encodeURIComponent(value)}`).join('&');

    if (!values) {
        return '';
    }

    return values.toLowerCase();
};

export const normaliseQueryString = (string) => {
    if (!string) {
        return null;
    }
    const formattedString = string.trim().replace(/^,*(.*?),*$/, '$1');
    return formattedString;
};

export const setOverflowForSidebar = (isOpen) => {
    document.documentElement.classList.toggle('overflow-hidden', isOpen);
};

// HORO-10509: fully removes the real page (`<main class="page">` — catalog,
// chat, whatever's underneath) from layout while a mobile auth screen is
// showing. Those screens (`.signUpScreen`/`.fullScreenAuth` in
// authModal.module.scss) are meant to be the *only* thing on screen, but
// they don't reserve their own space — they overlay the still-present page
// underneath, whose own content keeps contributing to the document's
// scrollable height. Scroll far enough (either because the page was
// already scrolled down when the modal opened, or the user just keeps
// scrolling once it's up) and there's nothing left of the auth screen to
// cover it — the real page shows through below. `display: none` (not
// `overflow: hidden` — see AuthModal.js's effect for why that's the wrong
// tool here) removes it from the document entirely, so there's nothing to
// scroll into past the auth screen's own content.
export const setPageHiddenBehindModal = (isHidden) => {
    const page = document.querySelector('main.page');
    if (page) {
        page.classList.toggle('hidden-behind-modal', isHidden);
    }
};

export const setRetinaClass = () => {
    if (window.devicePixelRatio > 1) {
        document.body.classList.add('retina');
    }
};

export const CheckRetinaDevice = () => {
    return window.devicePixelRatio > 1;
};

export const setMetaViewportContent = () => {
    const viewportTag = document.querySelector('#viewport');
    if (viewportTag && window.innerHeight > 650) {
        const viewportContent = viewportTag.getAttribute('content');
        viewportTag.setAttribute('content', `${viewportContent}, interactive-widget=resizes-content`);
    }
};

export const appHeight = (height) => {
    document.documentElement.style.setProperty('--app-height', `${height}px`);
};

// Path prefix the SPA mounts under for the current host. Centralised so
// BrowserRouter, the auth/redirect slice, and the API error handler all
// agree on the same answer (the three used to inline duplicate copies
// of the conditional, which silently disagreed for LAN-IP hosts).
//
//   localhost           → '/'   (local dev)
//   veda*.example.com   → '/'   (direct subdomain hosting)
//   192.168.x.x etc.    → '/'   (LAN testing from a phone)
//   anything else       → '/veda' (production: SPA served under /veda
//                                  on the main astrology.com domain)
//
// `''` and `'/'` behave identically as a `<BrowserRouter basename>` and
// as a `window.location` redirect target, so we use `'/'` for clarity.
export const getAppBasename = () => {
    const { hostname } = window.location;
    const isIpLiteral = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
    const isDirectHost = hostname.startsWith('veda') || hostname === 'localhost' || isIpLiteral;
    return isDirectHost ? '/' : '/veda';
};

export const setPageCssVariables = (page = '') => {
    switch (page) {
        case 'landing':
            document.documentElement.style.setProperty('--c-backgrounds', '#F7F7F7');
            document.documentElement.style.setProperty('--c-fonts', '#000000');
            break;
        case 'widget':
            document.documentElement.style.setProperty('--c-backgrounds', 'transparent');
            document.documentElement.style.setProperty('--c-fonts', '#ffffff');
            break;
        default:
            document.documentElement.style.setProperty('--c-backgrounds', '#101010');
            document.documentElement.style.setProperty('--c-fonts', '#ffffff');
    }
};

export const makeError = (message, status) => {
    let error = message instanceof Error ? message : new Error(message);
    error.status = status;
    return error;
};

export const validateChatId = (chatId) => {
    if (chatId.match(/^[0-9a-fA-F]{24}$/)) {
        return true;
    } else {
        return false;
    }
};

export const getClearString = (string) => {
    const markIndex = string.indexOf('?');

    if (markIndex !== -1) {
        return string.substring(0, markIndex);
    }

    return string;
};

export const timeCompare = (date1, date2) => {
    if (!date1 || !date2) {
        return false;
    }
    const differenceInSeconds = Math.abs((date2 - date1) / 1000);
    return !!(differenceInSeconds > CHAT_TIME_LIMIT_SECONDS);
};

// Pre-login (anonymous) message allowance is counted ACROSS all advisors/chats,
// not per chat. The counter object stays keyed by chatId (carry-over needs the
// per-chat breakdown), but the gate and the header display use the total.
export const getAnonMessagesUsed = (counters) => Object.values(counters || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

export const isAnonMessageLimitReached = (counters) => getAnonMessagesUsed(counters) >= CHAT_MAX_MESSAGES_UNREGISTER;

export const updateMessagesLimitCounter = (chatId = '', isIncrement = true) => {
    // Roll before incrementing: the first send of a new month must land on a
    // zeroed pool, not on top of last month's tally.
    ensureAnonMessagesRolled();
    let chatMessagesCounters = StorageService.get(STORAGE_KEY_CHATS_MESSAGES_COUNT);

    if (!chatMessagesCounters) {
        chatMessagesCounters = {
            [chatId]: 1
        };
    } else {
        const currentCount = chatMessagesCounters[chatId] || 0;
        chatMessagesCounters = {
            ...chatMessagesCounters,
            [chatId]: isIncrement ? currentCount + 1 : currentCount - 1
        };
    }

    StorageService.add(STORAGE_KEY_CHATS_MESSAGES_COUNT, chatMessagesCounters);
    store.dispatch(setChatHeaderCounter(getAnonMessagesUsed(chatMessagesCounters)));
};

export const updateResendData = (chatId = '', showResendBlockState = false, messageForResend = {}, isFulfilled = false) => {
    let resendData = StorageService.get(STORAGE_KEY_RESEND_MESSAGES) || {};

    if (messageForResend) {
        resendData = {
            ...resendData,
            [chatId]: { showResendBlockState, messageForResend }
        };
    } else {
        if (!resendData[chatId]) {
            resendData[chatId] = {};
        }
        resendData[chatId].showResendBlockState = showResendBlockState;
    }

    if (isFulfilled && resendData[chatId]) {
        delete resendData[chatId];
    }

    StorageService.add(STORAGE_KEY_RESEND_MESSAGES, resendData);
};

export const getResendData = (chatId = '') => {
    const resendData = StorageService.get(STORAGE_KEY_RESEND_MESSAGES) || {};
    return {
        showResendBlockState: resendData[`${chatId}`]?.showResendBlockState || false,
        messageForResend: resendData[`${chatId}`]?.messageForResend || ''
    };
};

export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const shortenText = (text = '', max) => {
    if (!max || !text) {
        return text;
    }
    return text && text.length > max ? text.slice(0, max).split(' ').slice(0, -1).join(' ') + '...' : text;
};

export const splitAt = (index, str) => [str.slice(0, index), str.slice(index)];

// HORO-10471: shared email validation.
//
// The only other email check in the app is inlined in ProfileForm and reads
// `/^[^\s@]+@[a-zA-Z]+\.[a-zA-Z]+$/`, which rejects perfectly valid addresses —
// anything with a multi-label TLD (`foo@mail.co.uk`), a hyphenated domain
// (`foo@my-domain.com`), or a subdomain. Don't reuse that one.
//
// This deliberately stays permissive: the server (Joi `.email()`) is the real
// authority, and the only job here is to catch obvious typos before a round
// trip. Over-strict client regexes lock real users out of a signup form.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export const isValidEmail = (value = '') => {
    const trimmed = String(value).trim();
    // 254 is the RFC 5321 ceiling for a whole address, and matches the cap the
    // waitlist endpoint enforces.
    return trimmed.length > 0 && trimmed.length <= 254 && EMAIL_PATTERN.test(trimmed);
};

// Normalize before sending: the backend's unique index only dedupes if every
// client agrees on casing and whitespace.
export const normalizeEmail = (value = '') => String(value).trim().toLowerCase();
